import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  format, parse, startOfWeek, getDay, addMonths, subMonths,
  isSameDay, isToday, startOfDay, endOfDay,
} from 'date-fns';
import { it } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Layout } from '../components/layout/Layout';
import { calendarAPI, clientsAPI, usersAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Phone, Video, MapPin, Pencil, Trash2, User, Users,
  SlidersHorizontal, X,
} from 'lucide-react';

// ── date-fns localizer ────────────────────────────────────────
const locales = { it };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: it }),
  getDay,
  locales,
});

// ── Config ───────────────────────────────────────────────────
const EVENT_TYPES = {
  appuntamento:      { label: 'Appuntamento',    icon: MapPin,       color: '#6366f1', bg: '#eef2ff', text: '#4338ca' },
  telefonata:        { label: 'Telefonata',      icon: Phone,        color: '#10b981', bg: '#ecfdf5', text: '#059669' },
  call:              { label: 'Video call',      icon: Video,        color: '#8b5cf6', bg: '#f5f3ff', text: '#7c3aed' },
  altro:             { label: 'Altro',           icon: CalendarIcon, color: '#64748b', bg: '#f8fafc', text: '#475569' },
  scadenza_dominio:  { label: 'Scad. dominio',  icon: CalendarIcon, color: '#f97316', bg: '#fff7ed', text: '#ea580c' },
  scadenza_contratto:{ label: 'Scad. contratto',icon: CalendarIcon, color: '#a855f7', bg: '#faf5ff', text: '#9333ea' },
};

const CALENDAR_TYPES = {
  personale: { label: 'Personale', color: '#6366f1' },
  condiviso:  { label: 'Condiviso',  color: '#f59e0b' },
};

// Desktop: mese / settimana / giorno — Mobile: solo mese / giorno
const VIEWS_DESKTOP = { month: 'Mese', week: 'Settimana', day: 'Giorno' };
const VIEWS_MOBILE  = { month: 'Mese', day: 'Giorno' };

const EMPTY_FORM = {
  titolo: '', descrizione: '', start: '', end: '',
  all_day: false, tipo: 'appuntamento',
  calendario: 'personale', assegnato_a: '', client_id: '',
};

// ── Helpers ──────────────────────────────────────────────────
const toLocalISO = (date) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth()+1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
};
const fmtDatetimeLocal = (iso) => {
  if (!iso) return '';
  try { return toLocalISO(new Date(iso)); } catch { return ''; }
};

const eventStyleGetter = (event) => {
  const cfg = EVENT_TYPES[event.tipo] || EVENT_TYPES.altro;
  return {
    style: {
      backgroundColor: cfg.color,
      borderLeft: event.calendario === 'condiviso' ? '3px solid #f59e0b' : `3px solid ${cfg.color}`,
      borderRadius: '4px',
      color: '#fff',
      fontSize: '11px',
      padding: '1px 5px',
      opacity: 0.92,
    },
  };
};

// ── Main Component ────────────────────────────────────────────
export const Calendario = () => {
  const [events, setEvents]           = useState([]);
  const [clients, setClients]         = useState([]);
  const [users, setUsers]             = useState([]);
  const [view, setView]               = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date()); // giorno selezionato (mobile)

  const [showPersonale, setShowPersonale] = useState(true);
  const [showCondiviso, setShowCondiviso] = useState(true);
  const [showScadenze, setShowScadenze]   = useState(true);
  const [filtersOpen, setFiltersOpen]     = useState(false); // dialog filtri mobile

  const [formOpen, setFormOpen]       = useState(false);
  const [detailOpen, setDetailOpen]   = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editMode, setEditMode]       = useState(false);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    Promise.all([clientsAPI.getAll(), usersAPI.getAll()])
      .then(([c, u]) => { setClients(c.data); setUsers(u.data); });
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const res = await calendarAPI.getEvents({ include_deadlines: true });
      setEvents(res.data.map((e) => ({
        ...e, start: new Date(e.start), end: new Date(e.end),
        title: e.titolo, allDay: e.all_day ?? e.allDay,
      })));
    } catch { toast.error('Errore nel caricamento eventi'); }
  };

  const visibleEvents = events.filter((e) => {
    if (e.calendario === 'personale' && !showPersonale) return false;
    if (e.calendario === 'condiviso'  && !showCondiviso)  return false;
    if (e.calendario === 'scadenze'   && !showScadenze)   return false;
    return true;
  });

  // eventi del giorno selezionato (per la lista mobile)
  const dayEvents = visibleEvents.filter(e => isSameDay(e.start, selectedDay));

  const openNew = useCallback((slotInfo = null) => {
    const start = slotInfo?.start ?? selectedDay;
    const end   = slotInfo?.end   ?? new Date(start.getTime() + 60 * 60 * 1000);
    setForm({ ...EMPTY_FORM, start: toLocalISO(start), end: toLocalISO(end) });
    setEditMode(false);
    setFormOpen(true);
  }, [selectedDay]);

  const openEdit = (event) => {
    setForm({
      titolo: event.titolo, descrizione: event.descrizione || '',
      start: fmtDatetimeLocal(event.start), end: fmtDatetimeLocal(event.end),
      all_day: event.all_day || false, tipo: event.tipo,
      calendario: event.calendario,
      assegnato_a: event.assegnato_a || '', client_id: event.client_id || '',
    });
    setSelectedEvent(event); setEditMode(true);
    setDetailOpen(false); setFormOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.titolo.trim())      { toast.error('Il titolo è obbligatorio'); return; }
    if (!form.start || !form.end) { toast.error('Inserisci data e ora'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        start: new Date(form.start).toISOString(),
        end:   new Date(form.end).toISOString(),
        assegnato_a: form.assegnato_a || null,
        client_id:   form.client_id   || null,
      };
      if (editMode && selectedEvent) {
        await calendarAPI.updateEvent(selectedEvent.id, payload);
        toast.success('Evento aggiornato');
      } else {
        await calendarAPI.createEvent(payload);
        toast.success('Evento creato');
      }
      await loadEvents();
      setFormOpen(false); setForm(EMPTY_FORM);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Eliminare "${event.titolo}"?`)) return;
    try {
      await calendarAPI.deleteEvent(event.id);
      toast.success('Evento eliminato');
      setDetailOpen(false); await loadEvents();
    } catch { toast.error('Errore nell\'eliminazione'); }
  };

  const handleSelectEvent = (event) => {
    if (event.readonly) return;
    setSelectedEvent(event);
    setDetailOpen(true);
  };

  // Selezione slot: su mobile imposta il giorno selezionato
  const handleSelectSlot = (slotInfo) => {
    setSelectedDay(slotInfo.start);
    if (view === 'month') {
      // Su mobile apre direttamente il form; su desktop lascia scegliere
      openNew(slotInfo);
    } else {
      openNew(slotInfo);
    }
  };

  const navigate = (dir) => {
    if (view === 'month') setCurrentDate(dir > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    else setCurrentDate(new Date(currentDate.getTime() + dir * (view === 'week' ? 7 : 1) * 86400000));
  };

  const navLabel = () => {
    if (view === 'month') return format(currentDate, 'MMMM yyyy', { locale: it });
    if (view === 'week') {
      const s = startOfWeek(currentDate, { locale: it });
      return `${format(s, 'dd MMM', { locale: it })} – ${format(new Date(s.getTime() + 6*86400000), 'dd MMM yyyy', { locale: it })}`;
    }
    return format(currentDate, 'EEEE dd MMMM yyyy', { locale: it });
  };

  const getUserName  = (id) => { if (!id) return '—'; const u = users.find(u=>u.id===id); return u ? `${u.nome} ${u.cognome}` : id; };
  const getClientName = (id) => { if (!id) return '—'; const c = clients.find(c=>c.id===id); return c?.nome || id; };
  const detailCfg = selectedEvent ? (EVENT_TYPES[selectedEvent.tipo] || EVENT_TYPES.altro) : null;

  // ── Pannello filtri (usato sia da sidebar desktop che dialog mobile) ──
  const FiltersContent = () => (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Calendari</p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: CALENDAR_TYPES.personale.color }} />
              <span className="text-sm text-slate-700">Personale</span>
            </div>
            <Switch checked={showPersonale} onCheckedChange={setShowPersonale} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: CALENDAR_TYPES.condiviso.color }} />
              <span className="text-sm text-slate-700">Condiviso</span>
            </div>
            <Switch checked={showCondiviso} onCheckedChange={setShowCondiviso} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: '#f97316' }} />
              <span className="text-sm text-slate-700">Scadenze</span>
            </div>
            <Switch checked={showScadenze} onCheckedChange={setShowScadenze} />
          </div>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tipi evento</p>
        <div className="space-y-2">
          {Object.entries(EVENT_TYPES).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: v.color }} />
              <span className="text-xs text-slate-600">{v.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <Layout title="Calendario">
      <style>{`
        .rbc-calendar { font-family: inherit; }
        .rbc-header { background:#f8fafc; color:#64748b; font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:.05em; padding:6px 2px; border-bottom:1px solid #e2e8f0; }
        .rbc-month-view, .rbc-time-view { border:none; border-radius:8px; overflow:hidden; background:white; }
        .rbc-day-bg + .rbc-day-bg { border-left:1px solid #f1f5f9; }
        .rbc-month-row + .rbc-month-row { border-top:1px solid #f1f5f9; }
        .rbc-off-range-bg { background:#fafafa; }
        .rbc-today { background:#eff6ff !important; }
        .rbc-date-cell { padding:3px 6px; font-size:12px; color:#475569; }
        .rbc-date-cell.rbc-now button { background:#6366f1; color:white; border-radius:50%; width:22px; height:22px; display:inline-flex; align-items:center; justify-content:center; }
        .rbc-show-more { color:#6366f1; font-size:10px; font-weight:600; padding:0 4px; }
        .rbc-time-slot { font-size:11px; color:#94a3b8; }
        .rbc-current-time-indicator { background:#ef4444; height:2px; }
        .rbc-toolbar { display:none; }
        .rbc-event { border:none !important; box-shadow:0 1px 2px rgba(0,0,0,.1); }
        .rbc-event:focus { outline:none; }
        .rbc-slot-selection { background:rgba(99,102,241,.15); }
        .rbc-time-content { border-top:1px solid #e2e8f0; }
        .rbc-timeslot-group { border-bottom:1px solid #f1f5f9; min-height:44px; }
        .rbc-row-segment { padding:0 2px 2px; }
        @media (max-width:640px) {
          .rbc-date-cell { padding:2px 3px; font-size:11px; }
          .rbc-header { font-size:10px; padding:4px 2px; }
          .rbc-show-more { font-size:9px; }
          .rbc-event { font-size:10px !important; padding:1px 3px !important; }
        }
      `}</style>

      {/* ── Layout principale ── */}
      <div className="flex gap-5 h-[calc(100vh-7rem)]">

        {/* ── Sidebar sinistra (solo desktop) ── */}
        <aside className="hidden lg:flex w-52 shrink-0 flex-col gap-4">
          <Button className="w-full bg-indigo-600 hover:bg-indigo-700 gap-2" onClick={() => openNew()}>
            <Plus className="h-4 w-4" /> Nuovo evento
          </Button>
          <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Oggi</p>
            <p className="text-2xl font-bold text-indigo-700">{format(new Date(), 'dd')}</p>
            <p className="text-sm text-slate-600 capitalize">{format(new Date(), 'EEEE, MMMM yyyy', { locale: it })}</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-4 bg-white">
            <FiltersContent />
          </div>
        </aside>

        {/* ── Colonna centrale (calendario + lista eventi mobile) ── */}
        <div className="flex-1 flex flex-col min-w-0 gap-3">

          {/* ── Toolbar ── */}
          <div className="flex items-center gap-2">
            {/* Navigazione */}
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="h-9 px-3 shrink-0" onClick={() => { setCurrentDate(new Date()); setSelectedDay(new Date()); }}>
              Oggi
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => navigate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>

            {/* Label mese — flex-1 per occupare lo spazio */}
            <h2 className="flex-1 text-base font-semibold text-slate-800 capitalize truncate px-1">{navLabel()}</h2>

            {/* Filtri (solo mobile) */}
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 lg:hidden" onClick={() => setFiltersOpen(true)}>
              <SlidersHorizontal className="h-4 w-4" />
            </Button>

            {/* Selettore vista */}
            <div className="flex gap-0.5 bg-slate-100 rounded-lg p-1 shrink-0">
              {Object.entries(
                // su schermi piccoli mostra solo Mese/Giorno
                window.innerWidth < 640 ? VIEWS_MOBILE : VIEWS_DESKTOP
              ).map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    view === v ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* + Nuovo evento (desktop lo ha nella sidebar, mobile lo mostra qui) */}
            <Button size="icon" className="h-9 w-9 bg-indigo-600 hover:bg-indigo-700 shrink-0 lg:hidden" onClick={() => openNew()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* ── Calendario ── */}
          <div className={`rounded-xl overflow-hidden border border-slate-200 shadow-sm ${
            // Su mobile lascia spazio per la lista eventi sotto
            view === 'month' ? 'lg:flex-1' : 'flex-1'
          }`} style={{ minHeight: view === 'month' ? 320 : undefined }}>
            <Calendar
              localizer={localizer}
              events={visibleEvents}
              view={view}
              onView={setView}
              date={currentDate}
              onNavigate={(d) => { setCurrentDate(d); setSelectedDay(d); }}
              selectable
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              culture="it"
              messages={{
                allDay: 'Giornata', previous: 'Prec.', next: 'Succ.',
                today: 'Oggi', month: 'Mese', week: 'Settimana', day: 'Giorno',
                agenda: 'Agenda', date: 'Data', time: 'Ora', event: 'Evento',
                showMore: (n) => `+${n}`,
                noEventsInRange: 'Nessun evento',
              }}
              style={{ height: view === 'month' ? 380 : '100%' }}
            />
          </div>

          {/* ── Lista eventi del giorno selezionato (visibile su mobile in vista mese) ── */}
          {view === 'month' && (
            <div className="lg:hidden rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700 capitalize">
                  {isToday(selectedDay)
                    ? 'Oggi'
                    : format(selectedDay, 'EEEE dd MMMM', { locale: it })}
                </p>
                <button
                  onClick={() => openNew()}
                  className="flex items-center gap-1 text-xs text-indigo-600 font-medium"
                >
                  <Plus className="h-3.5 w-3.5" /> Aggiungi
                </button>
              </div>
              {dayEvents.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-6">
                  Nessun evento — tocca + per aggiungerne uno
                </p>
              ) : (
                <div className="divide-y divide-slate-50">
                  {dayEvents.map(ev => {
                    const cfg = EVENT_TYPES[ev.tipo] || EVENT_TYPES.altro;
                    const Icon = cfg.icon;
                    return (
                      <button key={ev.id} onClick={() => handleSelectEvent(ev)}
                        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100">
                        <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                          style={{ background: cfg.bg }}>
                          <Icon className="h-4 w-4" style={{ color: cfg.color }} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{ev.titolo}</p>
                          <p className="text-xs text-slate-500">
                            {ev.all_day
                              ? 'Tutto il giorno'
                              : `${format(ev.start, 'HH:mm')} – ${format(ev.end, 'HH:mm')}`}
                            {ev.calendario === 'condiviso' && (
                              <span className="ml-2 text-amber-600 font-medium">• Condiviso</span>
                            )}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Dialog filtri (mobile) ── */}
      <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Filtri calendario</DialogTitle>
          </DialogHeader>
          <FiltersContent />
          <Button className="w-full bg-indigo-600 hover:bg-indigo-700 mt-2" onClick={() => setFiltersOpen(false)}>
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>

      {/* ── Form nuovo/modifica evento ── */}
      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setForm(EMPTY_FORM); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifica evento' : 'Nuovo evento'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1">
              <Label>Titolo *</Label>
              <Input autoFocus value={form.titolo}
                onChange={(e) => setForm(p => ({ ...p, titolo: e.target.value }))}
                placeholder="Es. Call con cliente" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm(p => ({ ...p, tipo: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(EVENT_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Calendario</Label>
                <Select value={form.calendario} onValueChange={(v) => setForm(p => ({ ...p, calendario: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personale">Personale</SelectItem>
                    <SelectItem value="condiviso">Condiviso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Inizio *</Label>
                <Input type="datetime-local" value={form.start}
                  onChange={(e) => setForm(p => ({ ...p, start: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Fine *</Label>
                <Input type="datetime-local" value={form.end}
                  onChange={(e) => setForm(p => ({ ...p, end: e.target.value }))} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="all-day" checked={form.all_day}
                onCheckedChange={(v) => setForm(p => ({ ...p, all_day: v }))} />
              <Label htmlFor="all-day" className="cursor-pointer">Tutto il giorno</Label>
            </div>
            {form.calendario === 'condiviso' && (
              <div className="space-y-1">
                <Label>Assegnato a</Label>
                <Select value={form.assegnato_a || 'none'}
                  onValueChange={(v) => setForm(p => ({ ...p, assegnato_a: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label>Cliente (opzionale)</Label>
              <Select value={form.client_id || 'none'}
                onValueChange={(v) => setForm(p => ({ ...p, client_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea rows={2} value={form.descrizione}
                onChange={(e) => setForm(p => ({ ...p, descrizione: e.target.value }))}
                placeholder="Descrizione o note aggiuntive..." />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving}>
                {saving ? 'Salvataggio…' : (editMode ? 'Salva' : 'Crea evento')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Dettaglio evento ── */}
      {selectedEvent && (
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {detailCfg && (
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                      style={{ background: detailCfg.bg }}>
                      <detailCfg.icon className="h-4 w-4" style={{ color: detailCfg.color }} />
                    </span>
                  )}
                  <DialogTitle className="text-base leading-snug">{selectedEvent.titolo}</DialogTitle>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(selectedEvent)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(selectedEvent)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-3 pt-1 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" style={{ color: detailCfg?.color, borderColor: detailCfg?.color }}>
                  {detailCfg?.label}
                </Badge>
                <Badge variant="outline" style={{
                  color: CALENDAR_TYPES[selectedEvent.calendario]?.color,
                  borderColor: CALENDAR_TYPES[selectedEvent.calendario]?.color,
                }}>
                  {CALENDAR_TYPES[selectedEvent.calendario]?.label}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
                <span>
                  {selectedEvent.all_day
                    ? format(selectedEvent.start, 'dd MMMM yyyy', { locale: it })
                    : `${format(selectedEvent.start, 'dd MMM yyyy HH:mm', { locale: it })} → ${format(selectedEvent.end, 'HH:mm', { locale: it })}`}
                </span>
              </div>
              {selectedEvent.assegnato_a && (
                <div className="flex items-center gap-2 text-slate-600">
                  <User className="h-4 w-4 shrink-0 text-slate-400" />
                  <span>{getUserName(selectedEvent.assegnato_a)}</span>
                </div>
              )}
              {selectedEvent.client_id && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Users className="h-4 w-4 shrink-0 text-slate-400" />
                  <span>{getClientName(selectedEvent.client_id)}</span>
                </div>
              )}
              {selectedEvent.descrizione && (
                <p className="text-slate-500 bg-slate-50 rounded-lg p-3 text-xs leading-relaxed">
                  {selectedEvent.descrizione}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};
